import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useConfiguration, useUpdateNoiseDoseConfiguration } from "@/hooks";
import { DEFAULT_OPTIONS } from "@/lib/constants";
import { useForm } from "react-hook-form";
import {
  NoiseDoseConfigurationRequest,
  NoiseDoseConfigurationRequestSchema,
} from "@/api";
import { zodResolver } from "@hookform/resolvers/zod";

// Import Shadcn Form components
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";

export function NoiseDoseConfiguration() {
  // query hook
  const { data: configuration, isLoading: configurationLoading } = useConfiguration();
  // mutation hooks
  const noiseDoseConfiguration = useUpdateNoiseDoseConfiguration();

  // form hooks
  const noiseDoseForm = useForm<NoiseDoseConfigurationRequest>({
    resolver: zodResolver(NoiseDoseConfigurationRequestSchema),
    values: {
      decibelRounding:
        configuration?.nDecibelRounding ??
        Number(DEFAULT_OPTIONS.NOISE_DOSE.DECIBEL_ROUNDING),
      doseRounding:
        configuration?.nDoseRounding ??
        Number(DEFAULT_OPTIONS.NOISE_DOSE.DOSE_ROUNDING),
      uncertaintyRounding:
        configuration?.nUncertaintyRounding ??
        Number(DEFAULT_OPTIONS.NOISE_DOSE.UNCERTAINTY_ROUNDING),
      criterionLevel:
        configuration?.nCriterionLevel ??
        Number(DEFAULT_OPTIONS.NOISE_DOSE.CRITERION_LEVEL),
      exchangeRate:
        configuration?.nExchangeRate ??
        Number(DEFAULT_OPTIONS.NOISE_DOSE.EXCHANGE_RATE),
      thresholdLevel:
        configuration?.nThresholdLevel ??
        Number(DEFAULT_OPTIONS.NOISE_DOSE.THRESHOLD_LEVEL),
      actionLevel:
        configuration?.nActionLevel ??
        Number(DEFAULT_OPTIONS.NOISE_DOSE.ACTION_LEVEL),
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuración de Ruido dosis</CardTitle>
        <CardDescription>
          Configure los parámetros para los reportes de dosis de ruido.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <Form {...noiseDoseForm}>
          <form className="grid gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={noiseDoseForm.control}
                name="criterionLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nivel de Criterio (dB)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Nivel de criterio permitido.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={noiseDoseForm.control}
                name="exchangeRate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tasa de Intercambio (dB)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Incremento de decibeles que duplica el riesgo.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={noiseDoseForm.control}
                name="actionLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nivel de Acción (dB)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Nivel que requiere implementar controles.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={noiseDoseForm.control}
                name="thresholdLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nivel de Umbral (dB)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Nivel por debajo del cual no se considera exposición.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <Separator className="my-2" />
            <h3 className="text-sm font-medium">
              Configuración de Precisión
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormField
                control={noiseDoseForm.control}
                name="decibelRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de Decibeles</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        min="0"
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para valores en dB.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={noiseDoseForm.control}
                name="doseRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de Dosis</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        min="0"
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para valores de dosis (%).
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={noiseDoseForm.control}
                name="uncertaintyRounding"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redondeo de Incertidumbre</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(Number(e.target.value))
                        }
                        min="0"
                        step="1"
                        className="w-24"
                      />
                    </FormControl>
                    <FormDescription>
                      Número de decimales para valores de incertidumbre.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                onClick={noiseDoseForm.handleSubmit(async (data) => {
                  await noiseDoseConfiguration.mutateAsync(data);
                })}
                disabled={configurationLoading || noiseDoseConfiguration.isPending}
                type="button"
              >
                {noiseDoseConfiguration.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
} 